'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, Download, ArrowRight } from 'lucide-react';
import FadeIn from '@/components/ui/FadeIn';
import Button from '@/components/ui/Button';
import ReadingProgress from '@/components/ui/ReadingProgress';

interface GuideArticleProps {
    title: string;
    desc: string;
    date: string;
    content: string;
    faq?: { question: string; answer: string }[];
    relatedLandingPage: {
        title: string;
        href: string;
    };
}

interface TOCItem {
    id: string;
    title: string;
}

const slugify = (text: string) => {
    return text
        .toLowerCase()
        .replace(/<[^>]*>?/gm, "") // remove HTML tags
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
};

export default function GuideArticle({
    title,
    desc,
    date,
    content,
    faq,
    relatedLandingPage,
}: GuideArticleProps) {
    const [activeId, setActiveId] = useState<string>('');
    const [toc, setToc] = useState<TOCItem[]>([]);
    const [enrichedContent, setEnrichedContent] = useState<string>('');

    useEffect(() => {
        // Run once on mount to parse content and inject IDs into h2 tags
        const extractedToc: TOCItem[] = [];
        const parsed = content.replace(/<h2>(.*?)<\/h2>/gi, (match, innerHtml) => {
            const cleanTitle = innerHtml.replace(/<[^>]*>?/gm, "");
            const id = slugify(cleanTitle);
            extractedToc.push({ id, title: cleanTitle });
            return `<h2 id="${id}" class="scroll-mt-32">${innerHtml}</h2>`;
        });

        if (faq && faq.length > 0) {
            extractedToc.push({ id: 'faq', title: 'Frequently Asked Questions' });
        }

        setToc(extractedToc);
        setEnrichedContent(parsed);
    }, [content, faq]);

    useEffect(() => {
        if (toc.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // Highlight the section when it crosses the top 20%-80% of the screen
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: '-10% 0px -80% 0px' }
        );

        toc.forEach((item) => {
            const element = document.getElementById(item.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [toc]);

    return (
        <>
            <ReadingProgress />

            <main className="min-h-screen bg-[var(--color-md-surface)] relative">

                {/* ── HERO ───────────────────────────────────────── */}
                <section className="relative pt-36 pb-24 md:pt-44 md:pb-24 overflow-hidden">

                    {/* Ambient glow */}
                    <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full bg-[var(--color-md-primary)]/[0.04] blur-[120px] pointer-events-none" />

                    <div className="container mx-auto px-6 max-w-5xl relative z-10">
                        {/* Semantic Breadcrumbs (LLM & Human Nav) */}
                        <FadeIn>
                            <nav aria-label="Breadcrumb" className="mb-12 md:mb-16">
                                <ol className="flex items-center gap-2 text-sm font-medium text-[var(--color-md-on-surface-variant)]">
                                    <li>
                                        <Link href="/" className="hover:text-[var(--color-md-primary)] transition-colors">
                                            Home
                                        </Link>
                                    </li>
                                    <li>
                                        <ChevronLeft className="w-3.5 h-3.5 rotate-180 opacity-50" />
                                    </li>
                                    <li>
                                        <Link href="/guides" className="hover:text-[var(--color-md-primary)] transition-colors">
                                            Knowledge Hub
                                        </Link>
                                    </li>
                                    <li>
                                        <ChevronLeft className="w-3.5 h-3.5 rotate-180 opacity-50" />
                                    </li>
                                    <li>
                                        <span className="text-[var(--color-md-on-surface)]" aria-current="page">
                                            {/* Truncate long titles on mobile */}
                                            <span className="block sm:hidden">{title.substring(0, 24)}...</span>
                                            <span className="hidden sm:block">{title}</span>
                                        </span>
                                    </li>
                                </ol>
                            </nav>
                        </FadeIn>

                        <FadeIn delay={0.05}>
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[var(--color-md-primary)]/[0.08] mb-8">
                                <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)]" />
                                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[var(--color-md-primary)]">
                                    Knowledge Hub
                                </span>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-black text-[var(--color-md-on-surface)] tracking-[-0.035em] leading-[1.08] mb-8 max-w-[24ch]">
                                {title}
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.15}>
                            <p className="text-lg md:text-xl text-[var(--color-md-on-surface-variant)] leading-relaxed max-w-[52ch] mb-12 font-medium">
                                {desc}
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <div className="flex items-center gap-4 text-sm text-[var(--color-md-on-surface-variant)]">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-[var(--color-md-primary)]/[0.1] flex items-center justify-center">
                                        <span className="text-xs font-black text-[var(--color-md-primary)]">W</span>
                                    </div>
                                    <span className="font-bold text-[var(--color-md-on-surface)]">WaVault Data Team</span>
                                </div>
                                <span className="text-[var(--color-md-outline)]">·</span>
                                <time className="font-medium">{date}</time>
                            </div>
                        </FadeIn>
                    </div>
                </section>

                {/* ── TWO COLUMN LAYOUT ──────────────────────────── */}
                <div className="container mx-auto px-6 max-w-5xl pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-12 lg:gap-24 relative">

                    {/* LEFT: Sticky Sidebar (TOC) */}
                    <aside className="hidden lg:block relative">
                        <div className="sticky top-32">
                            {toc.length > 0 && (
                                <>
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-on-surface-variant)] mb-6 opacity-60">
                                        On this page
                                    </h4>
                                    <nav className="flex flex-col gap-3.5 mb-12">
                                        {toc.map((item) => (
                                            <a
                                                key={item.id}
                                                href={`#${item.id}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const target = document.getElementById(item.id);
                                                    if (target) {
                                                        const yOffset = -100; // offset for sticky header
                                                        const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                                        window.scrollTo({ top: y, behavior: 'smooth' });
                                                        setActiveId(item.id);
                                                    }
                                                }}
                                                className={`text-[13px] font-bold leading-snug transition-all duration-300 border-l-2 pl-4 py-1
                                                    ${activeId === item.id
                                                        ? 'border-[var(--color-md-primary)] text-[var(--color-md-primary)]'
                                                        : 'border-transparent text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)] hover:border-[var(--color-md-outline)]'
                                                    }
                                                `}
                                            >
                                                {item.title}
                                            </a>
                                        ))}
                                    </nav>
                                </>
                            )}

                            {/* Trust Badge inside Sidebar */}
                            <div className="p-6 rounded-3xl bg-[var(--color-md-surface-variant)]/50 border border-[var(--color-md-outline)] shadow-sm">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-full bg-[var(--color-md-primary)]/[0.1] flex items-center justify-center">
                                        <Download className="w-4 h-4 text-[var(--color-md-primary)]" />
                                    </div>
                                    <span className="text-sm font-bold text-[var(--color-md-on-surface)]">Ready to export?</span>
                                </div>
                                <p className="text-xs text-[var(--color-md-on-surface-variant)] mb-4 font-medium leading-relaxed">
                                    Join 1,000+ professionals exporting WhatsApp contacts privately.
                                </p>
                                <Button
                                    href="https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi"
                                    variant="outline"
                                    className="w-full text-xs py-2.5 h-auto rounded-xl"
                                >
                                    Add to Chrome
                                </Button>
                            </div>
                        </div>
                    </aside>

                    {/* RIGHT: Article Content */}
                    <article className="min-w-0">
                        {/* ── Divider Top ── */}
                        <div className="h-px bg-[var(--color-md-outline)] mb-12 sm:mb-16" />

                        <FadeIn delay={0.1}>
                            <div
                                className="prose prose-lg prose-h2:scroll-mt-32 max-w-none 
                                text-[var(--color-md-on-surface)]/90 
                                prose-p:leading-relaxed prose-a:text-[var(--color-md-primary)] prose-a:font-semibold prose-a:no-underline hover:prose-a:underline
                                prose-h2:font-black prose-h2:tracking-tight prose-h2:text-[var(--color-md-on-surface)] prose-h2:mt-16 prose-h2:mb-6
                                prose-h3:font-bold prose-h3:tracking-tight prose-h3:text-[var(--color-md-on-surface)]
                                prose-strong:font-black prose-strong:text-[var(--color-md-on-surface)]
                                prose-li:marker:text-[var(--color-md-primary)]"
                                dangerouslySetInnerHTML={{ __html: enrichedContent || content }}
                            />
                        </FadeIn>

                        {/* FAQ Section */}
                        {faq && faq.length > 0 && (
                            <FadeIn delay={0.15}>
                                <div className="mt-16 border-t border-[var(--color-md-outline)] pt-12">
                                    <h2 id="faq" className="mt-0 text-[clamp(1.75rem,3vw,2.5rem)] font-black tracking-tight text-[var(--color-md-on-surface)] mb-8 scroll-mt-32">
                                        Frequently Asked Questions
                                    </h2>
                                    <div className="flex flex-col gap-6">
                                        {faq.map((item, i) => (
                                            <div key={i} className="p-6 md:p-8 rounded-3xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)]">
                                                <h3 className="text-xl font-bold text-[var(--color-md-on-surface)] mb-4 leading-tight">
                                                    {item.question}
                                                </h3>
                                                <p className="text-[var(--color-md-on-surface-variant)] leading-relaxed m-0 text-lg">
                                                    {item.answer}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </FadeIn>
                        )}
                    </article>
                </div>

                {/* ── CTA SECTION ────────────────────────────────── */}
                <section className="container mx-auto px-6 max-w-5xl pb-32">
                    <FadeIn delay={0.1}>
                        <div className="relative rounded-[32px] overflow-hidden bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)]">
                            <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        backgroundImage: 'radial-gradient(circle at 1.5px 1.5px, var(--color-md-on-surface) 1px, transparent 0)',
                                        backgroundSize: '32px 32px',
                                    }}
                                />
                            </div>

                            <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-[var(--color-md-primary)]/[0.06] blur-[100px] pointer-events-none" />

                            <div className="relative z-10 px-8 py-16 md:px-16 md:py-20 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[var(--color-md-primary)]/[0.1] mb-8 shadow-cinematic-1">
                                    <Download className="w-7 h-7 text-[var(--color-md-primary)]" />
                                </div>

                                <h3 className="text-3xl md:text-4xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-4 leading-tight">
                                    Start exporting now
                                </h3>

                                <p className="text-[var(--color-md-on-surface-variant)] mb-10 max-w-md mx-auto text-lg font-medium leading-relaxed">
                                    Install WaVault and export your WhatsApp contacts in seconds. Free to start.
                                </p>

                                <Button
                                    href="https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi"
                                    className="px-10 py-4 rounded-full font-bold text-lg btn-glow shadow-cinematic-3 hover:shadow-cinematic-4 transition-all"
                                >
                                    Add to Chrome — It&apos;s Free
                                </Button>

                                <div className="mt-8">
                                    <Link
                                        href={relatedLandingPage.href}
                                        className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-primary)] transition-colors group"
                                    >
                                        {relatedLandingPage.title}
                                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                </section>
            </main>
        </>
    );
}
